// Utility functions for detecting and handling authentication errors

/**
 * Checks if the current URL contains Azure AD error parameters
 * and redirects to custom error page if needed
 */
export const checkForUrlAuthErrors = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  
  // Check for error in URL parameters
  const error = urlParams.get('error');
  const errorDescription = urlParams.get('error_description');
  
  // Check for error in hash (some OAuth flows use hash)
  const hashParams = new URLSearchParams(hash.substring(1));
  const hashError = hashParams.get('error');
  const hashErrorDescription = hashParams.get('error_description');
  
  const finalError = error || hashError;
  const finalErrorDescription = errorDescription || hashErrorDescription;
  
  if (finalError || finalErrorDescription) {
    console.log('🚫 Authentication error detected in URL:', {
      error: finalError,
      description: finalErrorDescription
    });
    
    // Check if it's an external user error
    if (isExternalUserErrorFromUrl(finalError, finalErrorDescription)) {
      console.log('🚫 External user error detected, redirecting to custom error page');
      window.location.href = '/auth-error?type=external_user';
      return true;
    }
    
    // Check for other specific error types
    const errorType = categorizeAuthError(finalError, finalErrorDescription);
    if (errorType !== 'general') {
      console.log(`🚫 ${errorType} error detected, redirecting to custom error page`);
      window.location.href = `/auth-error?type=${errorType}`;
      return true;
    }
  }
  
  return false;
};

/**
 * Checks if the error indicates an external user access issue
 */
const isExternalUserErrorFromUrl = (error?: string | null, errorDescription?: string | null): boolean => {
  if (!error && !errorDescription) return false;
  
  const combinedError = `${error || ''} ${errorDescription || ''}`.toLowerCase();
  
  const externalUserPatterns = [
    'aadsts90072',
    'does not exist in tenant',
    'cannot access the application',
    'needs to be added as an external user',
    'from identity provider',
    'live.com',
    'gmail.com',
    'access_denied'
  ];
  
  return externalUserPatterns.some(pattern => combinedError.includes(pattern));
};

/**
 * Categorizes the auth error type for appropriate handling
 */
const categorizeAuthError = (error?: string | null, errorDescription?: string | null): string => {
  if (!error && !errorDescription) return 'general';
  
  const combinedError = `${error || ''} ${errorDescription || ''}`.toLowerCase();
  
  if (combinedError.includes('expired') || combinedError.includes('session')) {
    return 'expired';
  }
  
  if (combinedError.includes('permission') || combinedError.includes('unauthorized')) {
    return 'permission';
  }
  
  if (combinedError.includes('access_denied')) {
    return 'external_user';
  }
  
  return 'general';
};

/**
 * Checks if an error object indicates external user access issues
 */
export const isExternalUserError = (error: Error): boolean => {
  const errorMessage = error.message.toLowerCase();
  const errorString = error.toString().toLowerCase();
  
  const externalUserPatterns = [
    'aadsts90072',
    'does not exist in tenant',
    'cannot access the application',
    'needs to be added as an external user',
    'from identity provider',
    'live.com',
    'gmail.com'
  ];
  
  return externalUserPatterns.some(pattern => 
    errorMessage.includes(pattern) || errorString.includes(pattern)
  );
};
